import { Database, Award, ShoppingCart, Banknote, Users, Pill } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import type { DashboardEstadisticas, DashboardTab } from '../types'
import type { LucideIcon } from 'lucide-react'

interface StatsConfigItem {
  config_id: number
  titulo: string
  icono?: string
  tipo?: string
}

interface DashboardStatsProps {
  estadisticas: DashboardEstadisticas | undefined
  isLoading: boolean
  colorPrimario: string
  tabs?: DashboardTab[]
  statsConfig?: StatsConfigItem[]
  formatoMoneda?: boolean
  valorPuntoCanje?: number
}

/** Mapeo de keywords en nombre de tabla → icono */
const iconByKeyword: Record<string, LucideIcon> = {
  puntajes: Award,
  ventas: ShoppingCart,
  canjes: Banknote,
  usuario: Users,
  registros: Database,
}

/** Mapeo de nombre de icono (string) → componente Lucide */
const iconByName: Record<string, LucideIcon> = {
  database: Database,
  award: Award,
  'shopping-cart': ShoppingCart,
  banknote: Banknote,
  users: Users,
  pill: Pill,
}

function getIcon(tabla: string): LucideIcon {
  const lower = tabla.toLowerCase()
  for (const [kw, icon] of Object.entries(iconByKeyword)) {
    if (lower.includes(kw)) return icon
  }
  return Database
}

function getIconFromName(name?: string): LucideIcon {
  if (!name) return Database
  return iconByName[name] || Database
}

/** Genera estilos inline de gradiente usando colorPrimario */
function cardGradientStyle(colorPrimario: string) {
  return {
    background: `linear-gradient(to right, ${colorPrimario}26 0%, ${colorPrimario}0d 50%, transparent 100%)`,
  }
}

export function DashboardStats({ estadisticas, isLoading, colorPrimario, tabs, statsConfig, formatoMoneda, valorPuntoCanje }: DashboardStatsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-8 rounded-lg" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (!estadisticas) return null

  // Si hay stats_config, usar renderizado configurable
  if (statsConfig && statsConfig.length > 0) {
    return <StatsFromConfig estadisticas={estadisticas} colorPrimario={colorPrimario} statsConfig={statsConfig} formatoMoneda={formatoMoneda} valorPuntoCanje={valorPuntoCanje} />
  }

  // Fallback: lógica original por keywords
  return <StatsFromKeywords estadisticas={estadisticas} colorPrimario={colorPrimario} tabs={tabs} formatoMoneda={formatoMoneda} valorPuntoCanje={valorPuntoCanje} />
}

/** Renderiza cards según stats_config del dashboard_config JSON */
function StatsFromConfig({
  estadisticas,
  colorPrimario,
  statsConfig,
  formatoMoneda,
  valorPuntoCanje = 0.25,
}: {
  estadisticas: DashboardEstadisticas
  colorPrimario: string
  statsConfig: StatsConfigItem[]
  formatoMoneda?: boolean
  valorPuntoCanje?: number
}) {
  const totalPuntos = (estadisticas.puntos_actividad || 0) + (estadisticas.puntos_ventas || 0)

  const ventasFuente = estadisticas.fuentes.find((f) => f.tabla.toLowerCase().includes('ventas'))
  const hasConsumoIA = statsConfig.some((sc) => sc.tipo === 'tipos_medicamentos')
  const showConsumoIA = !hasConsumoIA && !!ventasFuente && !ventasFuente.error
  const totalCards = statsConfig.length + (showConsumoIA ? 1 : 0)

  const gridCols = totalCards <= 3
    ? 'grid-cols-1 md:grid-cols-3'
    : totalCards === 4
      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5'

  return (
    <div className={`grid ${gridCols} gap-4`}>
      {statsConfig.map((sc, idx) => {
        const Icon = getIconFromName(sc.icono)
        const fuente = estadisticas.fuentes.find((f) => f.config_id === sc.config_id)

        // Determinar valor según tipo especial o fuente
        let valor: React.ReactNode
        let detalle: React.ReactNode = null

        if (sc.tipo === 'puntos_totales') {
          valor = totalPuntos.toLocaleString()
          detalle = (
            <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
              <span>Actividad: {(estadisticas.puntos_actividad || 0).toLocaleString()}</span>
              <span>Ventas: {(estadisticas.puntos_ventas || 0).toLocaleString()}</span>
            </div>
          )
        } else if (sc.tipo === 'tipos_medicamentos') {
          const ventasFuente = estadisticas.fuentes.find((f) => f.tabla.toLowerCase().includes('ventas'))
          const totalFacturas = ventasFuente?.total ?? 0
          const costoTotal = totalFacturas * 0.1
          valor = totalFacturas.toLocaleString()
          detalle = (
            <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
              <span>Costo unitario: $0.10</span>
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {costoTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </span>
            </div>
          )
        } else if (fuente) {
          const tabla = fuente.tabla.toLowerCase()
          const isVentas = tabla.includes('ventas')
          const isCanjes = tabla.includes('canjes')

          if (fuente.error) {
            valor = <span className="text-red-500 text-sm">Error</span>
          } else if (isVentas && estadisticas.total_cantidad_ventas) {
            valor = formatoMoneda
              ? '$ ' + estadisticas.total_cantidad_ventas.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
              : estadisticas.total_cantidad_ventas.toLocaleString()
            detalle = (
              <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground whitespace-nowrap">
                <span>Facturas: {fuente.total.toLocaleString()}</span>
                <span>Meds: {(estadisticas.total_cantidad_ventas || 0).toLocaleString()}</span>
              </div>
            )
          } else if (isCanjes && !fuente.error) {
            const ptsCanjes = estadisticas.puntos_canjes ?? 0
            valor = fuente.total.toLocaleString()
            detalle = (
              <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                <span>{ptsCanjes.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} pts</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {(ptsCanjes * valorPuntoCanje).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </span>
              </div>
            )
          } else {
            valor = fuente.total.toLocaleString()
          }

          if (!fuente.error && !isCanjes && ((fuente.validados ?? 0) > 0 || (fuente.sin_validar ?? 0) > 0)) {
            detalle = (
              <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                <span className="text-emerald-600 dark:text-emerald-400">Validados: {(fuente.validados ?? 0).toLocaleString()}</span>
                <span className="text-red-500 dark:text-red-400">Pendiente: {(fuente.sin_validar ?? 0).toLocaleString()}</span>
              </div>
            )
          }

          if (!fuente.error && !isCanjes && ((fuente.meta ?? 0) > 0 || (fuente.en_proceso ?? 0) > 0)) {
            detalle = (
              <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                <span className="text-emerald-600 dark:text-emerald-400">Meta: {(fuente.meta ?? 0).toLocaleString()}</span>
                <span className="text-amber-700 dark:text-amber-600">En proceso: {(fuente.en_proceso ?? 0).toLocaleString()}</span>
              </div>
            )
          }
        } else {
          valor = '—'
        }

        return (
          <Card
            key={`${sc.config_id}-${idx}`}
            className="border border-gray-300 dark:border-gray-700"
            style={cardGradientStyle(colorPrimario)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{sc.tipo === 'tipos_medicamentos' ? 'Consumo IA' : sc.titulo}</CardTitle>
              <Icon className="h-4 w-4" style={{ color: colorPrimario }} />
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold">{valor}</div>
                {detalle}
              </div>
              {fuente?.error && (
                <p className="text-xs text-red-500 mt-1">{fuente.error}</p>
              )}
            </CardContent>
          </Card>
        )
      })}
      {showConsumoIA && ventasFuente && (() => {
        const totalFacturas = ventasFuente.total ?? 0
        const costoTotal = totalFacturas * 0.1
        return (
          <Card
            key="consumo-ia-auto"
            className="border border-gray-300 dark:border-gray-700"
            style={cardGradientStyle(colorPrimario)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Consumo IA</CardTitle>
              <Pill className="h-4 w-4" style={{ color: colorPrimario }} />
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold">{totalFacturas.toLocaleString()}</div>
                <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                  <span>Costo unitario: $0.10</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {costoTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })()}
    </div>
  )
}

/** Renderizado original basado en keywords de tabla (fallback) */
function StatsFromKeywords({
  estadisticas,
  colorPrimario,
  tabs,
  formatoMoneda,
  valorPuntoCanje = 0.25,
}: {
  estadisticas: DashboardEstadisticas
  colorPrimario: string
  tabs?: DashboardTab[]
  formatoMoneda?: boolean
  valorPuntoCanje?: number
}) {
  // IDs de config que están en los tabs (si se proporcionan)
  const tabConfigIds = tabs ? new Set(tabs.map((t) => t.config_id).filter(Boolean)) : null

  // Filtrar fuentes: solo las que están en tabs (si hay tabs)
  const fuentes = estadisticas.fuentes.filter((f) => {
    if (tabConfigIds && !tabConfigIds.has(f.config_id)) return false
    return true
  })

  // Ordenar: principal primero, luego según orden de tabs
  const tabOrder = tabs ? tabs.map((t) => t.config_id) : []
  fuentes.sort((a, b) => {
    if (a.rol === 'principal' && b.rol !== 'principal') return -1
    if (b.rol === 'principal' && a.rol !== 'principal') return 1
    const ia = tabOrder.indexOf(a.config_id)
    const ib = tabOrder.indexOf(b.config_id)
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib)
  })

  const totalPuntos = (estadisticas.puntos_actividad || 0) + (estadisticas.puntos_ventas || 0)

  const hasVentas = fuentes.some((f) => {
    const t = f.tabla.toLowerCase()
    const tit = (tabs?.find((tb) => tb.config_id === f.config_id)?.titulo || f.titulo || '').toLowerCase()
    return t.includes('ventas') || tit.includes('venta')
  })
  const showMedicamentos = hasVentas && (estadisticas.tipos_medicamentos || 0) > 0
  const totalCards = fuentes.length + (showMedicamentos ? 1 : 0)

  const gridCols = totalCards <= 3
    ? 'grid-cols-1 md:grid-cols-3'
    : totalCards === 4
      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5'

  return (
    <div className={`grid ${gridCols} gap-4`}>
      {fuentes.map((fuente) => {
        const tabla = fuente.tabla.toLowerCase()
        const isPuntajes = tabla.includes('puntajes')
        const isVentas = tabla.includes('ventas')
        const isCanjes = tabla.includes('canjes')
        const Icon = getIcon(fuente.tabla)

        // Título: usar el del tab si existe, sino el titulo de la fuente
        const tab = tabs?.find((t) => t.config_id === fuente.config_id)
        const titulo = tab?.titulo || fuente.titulo

        // Valor principal
        let valor: React.ReactNode = fuente.total.toLocaleString()
        if (fuente.error) {
          valor = <span className="text-red-500 text-sm">Error</span>
        } else if (isPuntajes) {
          valor = totalPuntos.toLocaleString()
        } else if (isVentas && estadisticas.total_cantidad_ventas) {
          valor = formatoMoneda
            ? '$ ' + estadisticas.total_cantidad_ventas.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
            : estadisticas.total_cantidad_ventas.toLocaleString()
        }

        return (
          <Card
            key={fuente.config_id}
            className="border border-gray-300 dark:border-gray-700"
            style={cardGradientStyle(colorPrimario)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{titulo}</CardTitle>
              <Icon className="h-4 w-4" style={{ color: colorPrimario }} />
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold">{valor}</div>
                {isPuntajes && !fuente.error && (
                  <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                    <span>Actividad: {(estadisticas.puntos_actividad || 0).toLocaleString()}</span>
                    <span>Ventas: {(estadisticas.puntos_ventas || 0).toLocaleString()}</span>
                  </div>
                )}
                {isCanjes && !fuente.error && (() => {
                  const ptsCanjes = estadisticas.puntos_canjes ?? 0
                  return (
                    <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                      <span>{ptsCanjes.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} pts</span>
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {(ptsCanjes * valorPuntoCanje).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                      </span>
                    </div>
                  )
                })()}
                {isVentas && !fuente.error && (
                  <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground whitespace-nowrap">
                    <span>Facturas: {fuente.total.toLocaleString()}</span>
                    <span>Meds: {(estadisticas.total_cantidad_ventas || 0).toLocaleString()}</span>
                  </div>
                )}
                {!fuente.error && !isCanjes && ((fuente.validados ?? 0) > 0 || (fuente.sin_validar ?? 0) > 0) && (
                  <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                    <span className="text-emerald-600 dark:text-emerald-400">Validados: {(fuente.validados ?? 0).toLocaleString()}</span>
                    <span className="text-red-500 dark:text-red-400">Pendiente: {(fuente.sin_validar ?? 0).toLocaleString()}</span>
                  </div>
                )}
                {!fuente.error && !isCanjes && ((fuente.meta ?? 0) > 0 || (fuente.en_proceso ?? 0) > 0) && (
                  <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                    <span className="text-emerald-600 dark:text-emerald-400">Meta: {(fuente.meta ?? 0).toLocaleString()}</span>
                    <span className="text-amber-700 dark:text-amber-600">En proceso: {(fuente.en_proceso ?? 0).toLocaleString()}</span>
                  </div>
                )}
              </div>
              {fuente.error && (
                <p className="text-xs text-red-500 mt-1">{fuente.error}</p>
              )}
            </CardContent>
          </Card>
        )
      })}
      {showMedicamentos && (() => {
        const ventasFuente = fuentes.find((f) => f.tabla.toLowerCase().includes('ventas'))
        const totalFacturas = ventasFuente?.total ?? 0
        const costoTotal = totalFacturas * 0.1
        return (
          <Card
            className="border border-gray-300 dark:border-gray-700"
            style={cardGradientStyle(colorPrimario)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Consumo IA</CardTitle>
              <Pill className="h-4 w-4" style={{ color: colorPrimario }} />
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold">{totalFacturas.toLocaleString()}</div>
                <div className="flex flex-col items-end gap-0.5 text-xs text-muted-foreground">
                  <span>Costo unitario: $0.10</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {costoTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })()}
    </div>
  )
}
